import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './InventoryCheck.module.css';
import { buildXrinvRequest, getMpxnError, isMpxnValid, parseSmetsVersion } from '../util';
import { atomProxyCommsCheckClient } from '../../api/atomProxyClient';
import { buildPostRequest } from '../../api/apiClient';
import { XrinvData } from '../../types/comms-check';

export interface InventoryCheckProps {
  onCompleted: (data: InventoryData) => void;
  onStarted: (mpxn: string) => void;
  triggerRef?: React.Ref<InventoryCheckRef>;
  inputMpxn?: string;
}

export interface InventoryCheckRef {
  trigger: () => void;
}

export interface InventoryData {
  address: string;
  data: XrinvData[];
  state: 'PROCESSING' | 'FAIL' | 'SUCCESS' | 'PROXY_L1_ERROR' | 'PROXY_L2_ERROR' | null;
}

const InventoryCheck: React.FC<InventoryCheckProps> = ({ onStarted, onCompleted, triggerRef, inputMpxn = '' }) => {
  const [mpxn, setMpxn] = React.useState<string>(inputMpxn);
  const [ui, setUi] = React.useState<boolean>(true);
  const [mpxnError, setMpxnError] = useState(false);
  const [mpxnErrorMessage, setMpxnErrorMessage] = useState('');
  const inProgress = useRef<boolean>(false);

  const xrinvXmlInitialTimeout = parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_INITIAL_TIMEOUT}`, 10);
  const xrinvXmlTimeout = parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_TIMEOUT}`, 10);
  const xrinvMaxRetries = parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_MAX_RETRIES}`, 10);
  let retriesXrinv = 0;

  const enterKeyHandler = (event: any) => {
    if (event.key === 'Enter') {
      // Prevent default behavior if needed (like form submission)
      event.preventDefault();

      startInventoryCheck();
    }
  };

  useImperativeHandle(triggerRef, () => ({
    trigger() {
      startInventoryCheck();
    },
  }));

  useEffect(() => {
    return () => {
      inProgress.current = false;
    };
  }, []);

  const validateMpxn = (value: string) => {
    if (!isMpxnValid(value)) {
      setMpxnError(true);
      setMpxnErrorMessage(getMpxnError(value));
      return false;
    }

    setMpxnError(false);
    return true;
  };

  const mpxnOnChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMpxn(event.target.value);
    validateMpxn(event.target.value);
  };

  const startInventoryCheck = () => {
    if (!validateMpxn(mpxn)) {
      return;
    }

    if (!inProgress.current) {
      sendSingleGenerateXmlXrinv();
    } else {
      console.log('Inventory Check already pending');
    }
  };

  function getAddress(l2Response: any): string {
    return (
      l2Response.atomL2response['0']?.xml_string?.Body?.XRINV_OUT_02?.InventoryDevice[
        '0'
      ]?.PropertyFilter?.AddressIdentifier?.replace(/,+/g, ' ') ?? ''
    );
  }

  function getXrinvData(l2Response: any): XrinvData[] {
    return l2Response.atomL2response['0']?.xml_string?.Body?.XRINV_OUT_02?.InventoryDevice?.map((device: any) => ({
      deviceId: device.DeviceID,
      deviceType: device.DeviceType,
      deviceStatus: device.DeviceStatus ? device.DeviceStatus : null,
      mpxn: device.ImportMPXN,
      smetsDeviceType: parseSmetsVersion(device.DeviceGBCSVersion),
    }));
  }

  const sendSingleGenerateXmlXrinv = () => {
    const request = buildXrinvRequest(mpxn);
    onStarted(mpxn);
    setUi(false);
    inProgress.current = true;
    atomProxyCommsCheckClient
      .generateXmlXrinv(request)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          onCompleted({
            address: '',
            data: [],
            state: 'PROXY_L1_ERROR',
          });
          inProgress.current = false;
          setUi(true);
        }
        return httpResponse.json();
      })
      .then((l1Response) => {
        if (inProgress.current) {
          setTimeout(() => sendSingleGetXmlXrinv(l1Response.atomL1response, mpxn), xrinvXmlInitialTimeout);
        }
      })
      .catch((e) => {
        console.warn('Unexpect L1 XRINV proxy error, L2 WONT start', e);
        onCompleted({
          address: '',
          data: [],
          state: 'PROXY_L1_ERROR',
        });
        inProgress.current = false;
        setUi(true);
      });
  };

  const sendSingleGetXmlXrinv = (data: any, mpxn: string) => {
    if (!inProgress.current) {
      return;
    }
    const xrinvGetXmlRequest = buildPostRequest(data);
    atomProxyCommsCheckClient
      .getXmlXrinv(xrinvGetXmlRequest)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          if (inProgress.current && xrinvMaxRetries > retriesXrinv) {
            retriesXrinv += 1;
            setTimeout(() => sendSingleGetXmlXrinv(data, mpxn), xrinvXmlTimeout);
            console.warn('L2 XRINV 500 proxy error, retrying');
          } else {
            inProgress.current = false;
            onCompleted({
              address: '',
              data: [],
              state: 'PROXY_L2_ERROR',
            });
            setUi(true);
            console.warn('L2 XRINV 500 proxy error, polling will end');
          }
        }
        return httpResponse.json();
      })
      .then((l2Response) => {
        if (l2Response.bolQueryState === 'FAIL') {
          onCompleted({
            address: '',
            data: [],
            state: 'FAIL',
          });
          inProgress.current = false;
          setUi(true);
        }

        if (l2Response.bolQueryState === 'PROCESSING') {
          if (inProgress.current && xrinvMaxRetries > retriesXrinv) {
            retriesXrinv += 1;
            setTimeout(() => sendSingleGetXmlXrinv(data, mpxn), xrinvXmlTimeout);
          } else {
            const xrinvData = getXrinvData(l2Response) || [];
            onCompleted({
              address: getAddress(l2Response),
              data: xrinvData,
              state: 'PROCESSING',
            });
            inProgress.current = false;
            setUi(true);
          }
        }

        if (l2Response.bolQueryState === 'SUCCESS') {
          const xrinvData: XrinvData[] = getXrinvData(l2Response) || [];
          onCompleted({
            address: getAddress(l2Response),
            data: xrinvData,
            state: 'SUCCESS',
          });
          inProgress.current = false;
          setUi(true);
        }
      })
      .catch((e) => {
        inProgress.current = false;
        onCompleted({
          address: '',
          data: [],
          state: 'PROXY_L2_ERROR',
        });
        setUi(true);
        console.warn(`L2 XRINV 500 proxy error, polling will end, ${e}`);
      });
  };

  return (
    <div>
      {ui && (
        <div className={styles.formContainer}>
          <label htmlFor={'inventoryCheckMpxnInput'}>What is the MPxN for this address?</label>
          <div className={styles.inputContainer}>
            <input
              id={'inventoryCheckMpxnInput'}
              type="text"
              placeholder={'Enter MPxN'}
              className={styles.inputField}
              onKeyDown={enterKeyHandler}
              value={mpxn}
              onChange={mpxnOnChangeHandler}
            />
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={startInventoryCheck}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.502 14H14.708L14.432 13.726C15.407 12.589 16 11.115 16 9.50003C16 5.91003 13.09 3.00003 9.5 3.00003C5.91 3.00003 3 5.91003 3 9.50003C3 13.09 5.91 16 9.5 16C11.115 16 12.588 15.408 13.725 14.434L14.001 14.708V15.5L18.999 20.491L20.49 19L15.502 14ZM9.5 14C7.014 14 5 11.986 5 9.50003C5 7.01503 7.014 5.00003 9.5 5.00003C11.985 5.00003 14 7.01503 14 9.50003C14 11.986 11.985 14 9.5 14Z"
                fill="#555C6B"
              />
            </svg>
          </div>
          {mpxnError && <span className="errorMessage">{mpxnErrorMessage}</span>}
        </div>
      )}
    </div>
  );
};

export default InventoryCheck;
